import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation, useSearchParams, Link } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { Helmet } from 'react-helmet-async'
import { getAssetUrl } from '../hooks/useAsset'

// Context Providers
import { AuthProvider } from './contexts/AuthContext'
import { NotificationProvider } from './contexts/NotificationContext'
import { PaymentProvider } from './contexts/PaymentContext'
import { CartProvider } from './contexts/CartContext'

// Components
import SecretHeader from './components/layout/SecretHeader'
import SecretFooter from './components/layout/SecretFooter'
import SecretPublicFooter from './components/SecretPublicFooter'
import LoadingSpinner from './components/ui/LoadingSpinner'
import ThemeToggle from '../components/ThemeToggle.jsx'

// PWA Components
import InstallPWAPrompt from './components/pwa/InstallPWAPrompt'
import OfflineIndicator from './components/pwa/OfflineIndicator'
import UpdateNotification from './components/pwa/UpdateNotification'

// PWA Utilities
import { initPWA } from './lib/pwa'

// Pages
import LandingPage from './pages/LandingPage'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage'
import ResetPasswordPage from './pages/auth/ResetPasswordPage'
import VerifyPage from './pages/auth/VerifyPage'
import PhoneVerifyPage from './pages/auth/PhoneVerifyPage'
import AuthCallbackPage from './pages/auth/AuthCallbackPage'
import DashboardPage from './pages/DashboardPage'
import EventsPage from './pages/EventsPage'
import EventDetailsPage from './pages/EventDetailsPage'
import OrdersPage from './pages/OrdersPage'
import OrderDetailsPage from './pages/OrderDetailsPage'
import ProfilePage from './pages/ProfilePage'
import TransferPage from './pages/TransferPage'
import NotificationsPage from './pages/NotificationsPage'
import CheckoutPage from './pages/CheckoutPage'
import AdminPage from './pages/admin/AdminPage'
import QRVerificationPage from './pages/QRVerificationPage'
import QRPage from './pages/QRPage'

// Shared Legal Pages
import PrivacyPolicy from '../components/PrivacyPolicy'
import TermsOfUse from '../components/TermsOfUse'
import TermsOfSale from '../components/TermsOfSale'

// Auth Hook
import { useAuth } from './contexts/AuthContext'

// Root Route Component - redirects authenticated users to dashboard
const RootRoute: React.FC = () => {
  const { isAuthenticated, loading } = useAuth()
  const [searchParams] = useSearchParams()

  // Check if this is an email verification callback landing on root
  const code = searchParams.get('code')
  const token = searchParams.get('token')
  const tokenHash = searchParams.get('token_hash')
  const error = searchParams.get('error')
  const errorCode = searchParams.get('error_code')
  
  // Any auth-related parameters should go to callback handler
  const hasAuthParams = code || token || tokenHash || error || errorCode
  
  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // If this has any auth-related parameters, redirect to proper handler
  if (hasAuthParams) {
    return <Navigate to={`/auth/callback?${searchParams.toString()}`} replace />
  }

  // If user is authenticated, redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  // If not authenticated, show landing page
  return (
    <PublicLayout>
      <LandingPage />
    </PublicLayout>
  )
}

// Auth Route Component - redirects authenticated users to dashboard
const AuthRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // If user is fully authenticated (including email verification), redirect to dashboard
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }

  // If not fully authenticated, show the auth page (login/register)
  return <>{children}</>
}

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()
  const location = useLocation()

  if (import.meta.env.DEV) {
    console.log('[ProtectedRoute] Render:', {
      path: location.pathname,
      isAuthenticated,
      loading
    })
  }

  // Only block on initial loading, not auth transitions
  if (loading) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] Showing loading spinner')
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-background">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!isAuthenticated) {
    if (import.meta.env.DEV) console.log('[ProtectedRoute] Not authenticated, redirecting to /')
    return <Navigate to="/" state={{ from: location }} replace />
  }

  if (import.meta.env.DEV) console.log('[ProtectedRoute] Rendering children')
  // Show content immediately, even during authTransitioning
  return <>{children}</>
}

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, isAdmin } = useAuth()
  const location = useLocation()

  if (import.meta.env.DEV) {
    console.log('[AdminRoute] Render:', {
      path: location.pathname,
      hasUser: !!user,
      isAdmin,
      loading
    })
  }

  if (loading) {
    if (import.meta.env.DEV) console.log('[AdminRoute] Showing loading spinner')
    return (
      <div className="min-h-screen-safe flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!user) {
    if (import.meta.env.DEV) console.log('[AdminRoute] No user, redirecting to /login')
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (!isAdmin) {
    if (import.meta.env.DEV) console.log('[AdminRoute] Not admin, redirecting to /dashboard')
    return <Navigate to="/dashboard" replace />
  }

  if (import.meta.env.DEV) console.log('[AdminRoute] Rendering admin children')
  return <>{children}</>
}

// Layout wrapper for authenticated pages
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="min-h-screen-safe flex flex-col">
      <SecretHeader />
      <main className="flex-1">
        {children}
      </main>
      <SecretFooter />
    </div>
  )
}

// Layout wrapper for public pages
const PublicLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  
  // Don't show home button on login and register pages as they have their own back buttons
  const hideHomeButton = location.pathname === '/login' || location.pathname === '/register'
  
  // Handle home button click - refresh if on secret landing page, otherwise navigate
  const handleHomeClick = (e: React.MouseEvent) => {
    if (location.pathname === '/') {
      e.preventDefault()
      window.location.reload()
    }
  }
  
  return (
    <div className="min-h-screen-safe flex flex-col">
      <main className="flex-1">
        {children}
      </main>
      <SecretPublicFooter />
    </div>
  )
}

const SecretApp: React.FC = () => {
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    // Initialize app
    const initializeApp = async () => {
      try {
        // Check for saved theme preference
        const savedTheme = localStorage.getItem('secret-theme')
        if (savedTheme) {
          document.documentElement.setAttribute('data-theme', savedTheme)
        }

        // Initialize PWA (service worker, offline support, install prompt)
        await initPWA()

        setIsInitialized(true)
      } catch (error) {
        console.error('App initialization failed:', error)
        setIsInitialized(true) // Still allow app to load
      }
    }

    initializeApp()

    // Track when tab was hidden to detect stale connections
    let hiddenAt: number | null = null

    const handleVisibilityChange = () => {
      if (import.meta.env.DEV) {
        console.log('[SecretApp] Tab visibility changed:', {
          hidden: document.hidden,
          visibilityState: document.visibilityState,
          timestamp: new Date().toISOString()
        })
      }

      if (document.hidden) {
        hiddenAt = Date.now()
      } else if (hiddenAt) {
        const hiddenDuration = Date.now() - hiddenAt
        if (import.meta.env.DEV) console.log('[SecretApp] Tab was hidden for:', hiddenDuration, 'ms')

        // If tab was hidden for more than 5 minutes, reload to clear stale Supabase connections
        // This prevents database queries from hanging after long tab switches
        // 5 minute threshold prevents reload during normal navigation and short breaks
        // Skip reload if user has items in cart to prevent cart loss
        if (hiddenDuration > 300000) {
          const savedCart = localStorage.getItem('secret-cart')
          const hasCartItems = savedCart && JSON.parse(savedCart).length > 0

          if (!hasCartItems) {
            if (import.meta.env.DEV) console.log('[SecretApp] Reloading page to clear stale Supabase connection')
            window.location.reload()
          } else {
            if (import.meta.env.DEV) console.log('[SecretApp] Skipping reload - user has items in cart')
          }
        }

        hiddenAt = null
      }
    }

    const handleFocus = () => {
      if (import.meta.env.DEV) console.log('[SecretApp] Window focused at:', new Date().toISOString())
    }

    const handleBlur = () => {
      if (import.meta.env.DEV) console.log('[SecretApp] Window blurred at:', new Date().toISOString())
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
    }
  }, [])

  if (!isInitialized) {
    return (
      <div className="min-h-screen-safe flex items-center justify-center bg-secret-bg-primary">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <AuthProvider>
      <NotificationProvider>
        <CartProvider>
          <PaymentProvider>
            <Helmet>
              <title>SECRET - Exclusive Tailtations® Pre-Orders</title>
              <meta name="description" content="Join SECRET for exclusive access to Tailtations popup events. Pre-order premium deep-fried lobster tails and skip the line." />
              <link rel="icon" type="image/png" href={getAssetUrl('/Tailtations-Secret-Favicon.png')} />

              {/* PWA Manifest */}
              <link rel="manifest" href="/secret-manifest.json" />
              <meta name="theme-color" content="#8a3134" />
              <meta name="apple-mobile-web-app-capable" content="yes" />
              <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
              <meta name="apple-mobile-web-app-title" content="SECRET" />
              <link rel="apple-touch-icon" href={getAssetUrl('/Tailtations-Secret-Favicon.png')} />

              {/* Open Graph tags */}
              <meta property="og:title" content="SECRET - Exclusive Tailtations Events" />
              <meta property="og:description" content="Join SECRET for exclusive access to Tailtations popup events. Pre-order premium deep-fried lobster tails and skip the line." />
              <meta property="og:image" content={`https://secret.tailtations.com${getAssetUrl('/Tailtations-Secret-Favicon.png')}`} />
              <meta property="og:url" content="https://secret.tailtations.com" />
              <meta property="og:type" content="website" />

              {/* Twitter Card tags */}
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="twitter:title" content="SECRET - Exclusive Tailtations Events" />
              <meta name="twitter:description" content="Join SECRET for exclusive access to Tailtations popup events. Pre-order premium deep-fried lobster tails and skip the line." />
              <meta name="twitter:image" content={`https://secret.tailtations.com${getAssetUrl('/Tailtations-Secret-Favicon.png')}`} />
            </Helmet>
            <div className="secret-app">
          <Routes>
            {/* Root Route - with authentication check */}
            <Route path="/" element={<RootRoute />} />
            
            <Route path="/login" element={
              <AuthRoute>
                <PublicLayout>
                  <LoginPage />
                </PublicLayout>
              </AuthRoute>
            } />
            
            <Route path="/register" element={
              <AuthRoute>
                <PublicLayout>
                  <RegisterPage />
                </PublicLayout>
              </AuthRoute>
            } />
            
            <Route path="/forgot-password" element={
              <PublicLayout>
                <ForgotPasswordPage />
              </PublicLayout>
            } />

            <Route path="/reset-password" element={
              <PublicLayout>
                <ResetPasswordPage />
              </PublicLayout>
            } />
            
            <Route path="/verify" element={
              <PublicLayout>
                <VerifyPage />
              </PublicLayout>
            } />

            <Route path="/verify-phone" element={
              <PublicLayout>
                <PhoneVerifyPage />
              </PublicLayout>
            } />

            <Route path="/auth/callback" element={
              <PublicLayout>
                <AuthCallbackPage />
              </PublicLayout>
            } />

            {/* Transfer page (can be accessed by non-users) */}
            <Route path="/transfer/:token" element={
              <PublicLayout>
                <TransferPage />
              </PublicLayout>
            } />

            {/* QR Verification page (public - for pickup staff) */}
            <Route path="/qr-verify" element={
              <PublicLayout>
                <QRVerificationPage />
              </PublicLayout>
            } />

            {/* QR Code Marketing Page (public) */}
            <Route path="/qr" element={<QRPage />} />

            {/* Legal Pages (public - shared components) */}
            <Route path="/terms-of-use" element={
              <PublicLayout>
                <TermsOfUse />
              </PublicLayout>
            } />

            <Route path="/privacy-policy" element={
              <PublicLayout>
                <PrivacyPolicy />
              </PublicLayout>
            } />

            <Route path="/terms-of-sale" element={
              <PublicLayout>
                <TermsOfSale />
              </PublicLayout>
            } />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <DashboardPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/events" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <EventsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/events/:eventId" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <EventDetailsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/orders" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <OrdersPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/orders/:orderId" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <OrderDetailsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/profile" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <ProfilePage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/notifications" element={
              <ProtectedRoute>
                <AuthenticatedLayout>
                  <NotificationsPage />
                </AuthenticatedLayout>
              </ProtectedRoute>
            } />

            <Route path="/checkout" element={
              <ProtectedRoute>
                <CheckoutPage />
              </ProtectedRoute>
            } />

            {/* Admin Routes */}
            <Route path="/admin/*" element={
              <AdminRoute>
                <AuthenticatedLayout>
                  <AdminPage />
                </AuthenticatedLayout>
              </AdminRoute>
            } />

            {/* Fallback Route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>

          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#ffffff',
                color: '#1a1a1a',
                border: '1px solid #e5e7eb',
                fontSize: '12px',
                fontWeight: '400',
                padding: '12px',
                maxWidth: '384px',
                width: '384px',
                lineHeight: '1.4',
                borderRadius: '8px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              },
              success: {
                iconTheme: {
                  primary: '#8a3134',
                  secondary: '#ffffff',
                },
                style: {
                  background: '#ffffff',
                  color: '#8a3134',
                  border: '1px solid #8a3134',
                  padding: '12px',
                  maxWidth: '384px',
                  width: '384px',
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '1.4',
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
              },
              error: {
                iconTheme: {
                  primary: '#962831',
                  secondary: '#ffffff',
                },
                style: {
                  background: '#ffffff',
                  color: '#962831',
                  border: '1px solid #962831',
                  padding: '12px',
                  maxWidth: '384px',
                  width: '384px',
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '1.4',
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
              },
              loading: {
                iconTheme: {
                  primary: '#d4af37',
                  secondary: '#ffffff',
                },
                style: {
                  background: '#ffffff',
                  color: '#1a1a1a',
                  border: '1px solid #d4af37',
                  padding: '12px',
                  maxWidth: '384px',
                  width: '384px',
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '1.4',
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
              },
              custom: {
                style: {
                  background: '#ffffff',
                  color: '#1a1a1a',
                  border: '1px solid #e5e7eb',
                  padding: '12px',
                  maxWidth: '384px',
                  width: '384px',
                  fontSize: '12px',
                  fontWeight: '400',
                  lineHeight: '1.4',
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                },
              },
            }}
          />

          {/* PWA Components */}
          <InstallPWAPrompt position="bottom" autoShow={true} />
          <OfflineIndicator position="top" showOnlineMessage={true} />
          <UpdateNotification position="top" autoUpdate={false} />
            </div>
          </PaymentProvider>
        </CartProvider>
      </NotificationProvider>
    </AuthProvider>
  )
}

export default SecretApp